import { CalendarClock, CalendarOff, Mail } from 'lucide-react';
import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { contactEmail, contactPhone, contactPhoneLabel } from '@/config/contact';
import { liveGuideAvailability, type GuideDiaryType } from '@/hooks/useGuideAvailability';
import { cn } from '@/lib/utils';
import {
  formatGuideExperience,
  formatGuidingSince,
  guideSpecialtyLabels,
  guideTourOptions,
  type GuideTourOptionType,
  type GuideType,
} from '@/types/types_guide';
import { formatCalendarDate } from '@/util/util_format';
import { GuideAvailabilityCalendar } from './GuideAvailabilityCalendar';

interface GuideProfileDialogProps {
  guide: GuideType | null;
  /** This guide's diary, or undefined if they are not linked to /portal. */
  diary: GuideDiaryType | undefined;
  fromDate: string;
  toDate: string;
  loadingAvailability: boolean;
  availabilityError: string | null;
  onClose: () => void;
}

/**
 * A tour leader's full profile, next to their diary.
 *
 * The two halves of the original page's modal: who the guide is on the left,
 * when they are free on the right. Picking a free day then offers the tour
 * lengths — a preference the partner states rather than something the day
 * constrains, since /portal answers in whole days (see `guideTourOptions`).
 */
export function GuideProfileDialog({
  guide,
  diary,
  fromDate,
  toDate,
  loadingAvailability,
  availabilityError,
  onClose,
}: GuideProfileDialogProps) {
  const [date, setDate] = useState('');
  const [option, setOption] = useState<GuideTourOptionType | null>(null);

  // A fresh selection per guide: a date that suited one guide says nothing about
  // the next, and carrying it over would show a chosen day the new diary has as
  // booked. Reset during render rather than in an effect, so the dialog never
  // paints one guide's date under another's name.
  const [lastGuideId, setLastGuideId] = useState(guide?.id);
  if (guide?.id !== lastGuideId) {
    setLastGuideId(guide?.id);
    setDate('');
    setOption(null);
  }

  if (!guide) return null;

  // A date is only selectable if it is free, so this holds for anything chosen.
  const options = date && diary?.has(date) ? guideTourOptions : [];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      {/* Wider than the other dialogs, and split a third / two thirds rather than
          two fifths / three fifths: the calendar is the working half of this
          screen and the profile beside it is a column of short lines.

          `min-w-0` on both halves is what makes those fractions binding. A flex
          item defaults to `min-width: auto`, meaning it refuses to shrink below
          its content — so a wide calendar widened its own column past two thirds
          and pushed the dialog out with it, instead of being clipped and
          scrolled by the wrapper inside. */}
      {/* `sm:max-w-6xl`, not `max-w-6xl`. `DialogContent` ships a default
          `sm:max-w-md`, and tailwind-merge only reconciles classes within the
          same variant — an unprefixed width sits in a different group, survives
          alongside it, and then loses to it in the cascade from `sm` up. Every
          other dialog here takes the 28rem default, so this is the first place
          it bites. Left unprefixed below `sm` so the base
          `max-w-[calc(100%-2rem)]` keeps its gutters on a phone. */}
      <DialogContent className='tw:max-h-[90vh] tw:sm:max-w-4xl tw:overflow-hidden tw:overflow-y-auto tw:p-0'>
        <div className='tw:flex tw:flex-col tw:md:flex-row'>
          {/* Profile */}
          <div className='tw:w-full tw:min-w-0 tw:bg-ed-cream tw:p-8 tw:md:w-1/3'>
            {guide.photo && (
              <img
                src={guide.photo}
                alt={guide.name}
                className='tw:mb-6 tw:h-64 tw:w-full tw:rounded-sm tw:object-cover'
              />
            )}
            <DialogTitle className='tw:font-serif tw:text-3xl tw:font-light tw:text-ed-black'>
              {guide.name}
            </DialogTitle>
            {guide.nameJp && <p className='tw:text-ed-stone'>{guide.nameJp}</p>}
            {/* Radix wants every dialog described, and the visible page already
                says all of this at length — so this is for screen readers only,
                and unconditional: a guide with no biography still needs one. */}
            <DialogDescription className='tw:sr-only'>
              Profile and availability for {guide.name}.
            </DialogDescription>

            {guide.specialties.length > 0 && (
              <div className='tw:mt-6'>
                <h4 className='tw:mb-2 tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>Specialties</h4>
                <div className='tw:flex tw:flex-wrap tw:gap-2'>
                  {guide.specialties.map((s) => (
                    <span key={s} className='tw:rounded-full tw:bg-white tw:px-3 tw:py-1 tw:text-sm tw:text-ed-charcoal'>
                      {guideSpecialtyLabels[s]}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {guide.bio && (
              <div className='tw:mt-6'>
                <h4 className='tw:mb-2 tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>About</h4>
                <p className='tw:text-ed-charcoal tw:leading-relaxed'>{guide.bio}</p>
              </div>
            )}

            <div className='tw:mt-6 tw:grid tw:grid-cols-2 tw:gap-4'>
              <div className='tw:rounded-sm tw:bg-white tw:p-4'>
                <h4 className='tw:mb-1 tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>Languages</h4>
                <p className='tw:text-ed-black'>{guide.languages.join(', ') || '—'}</p>
              </div>
              <div className='tw:rounded-sm tw:bg-white tw:p-4'>
                <h4 className='tw:mb-1 tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>Experience</h4>
                <p className='tw:text-ed-black'>{formatGuideExperience(guide.guidingSince)}</p>
                {guide.guidingSince && (
                  <p className='tw:mt-0.5 tw:text-xs tw:text-ed-stone'>{formatGuidingSince(guide.guidingSince)}</p>
                )}
              </div>
            </div>
          </div>

          {/* Availability */}
          <div className='tw:w-full tw:min-w-0 tw:p-8 tw:md:w-2/3'>
            <h3 className='tw:font-serif tw:text-2xl tw:text-ed-black'>Check Availability</h3>
            <p className='tw:mt-1 tw:mb-6 tw:text-sm tw:text-ed-stone'>
              {liveGuideAvailability
                ? `Select a free date to request a tour with ${guide.name.split(' ')[0]}.`
                : `We will check ${guide.name.split(' ')[0]}’s diary for you and come straight back.`}
            </p>

            {/* Order matters twice over. The flag comes first because with it
                off nothing was ever asked, so every state below is about a
                lookup that did not happen. Then loading before `!diary`: while
                the call is in flight there is no diary yet, and testing for one
                first would tell every visitor the guide was unlinked for as long
                as the call took. */}
            {!liveGuideAvailability ? (
              <ComingSoonAvailability guide={guide} fromDate={fromDate} toDate={toDate} />
            ) : loadingAvailability ? (
              <p className='tw:py-8 tw:text-center tw:text-ed-stone'>Loading availability…</p>
            ) : availabilityError ? (
              <Notice icon={CalendarOff} tone='red'>
                {availabilityError}
              </Notice>
            ) : !diary ? (
              <Notice icon={CalendarOff} tone='stone'>
                Availability for {guide.name} is on request — contact us and we will check the diary for you.
              </Notice>
            ) : (
              <GuideAvailabilityCalendar
                diary={diary}
                fromDate={fromDate}
                toDate={toDate}
                value={date}
                onChange={(next) => {
                  setDate(next);
                  setOption(null);
                }}
              />
            )}

            {date && (
              <div className='tw:mt-8 tw:border-t tw:border-ed-umber/10 tw:pt-6'>
                <h4 className='tw:font-serif tw:text-lg tw:text-ed-black'>{formatCalendarDate(date)}</h4>

                {options.length === 0 ? (
                  <p className='tw:mt-2 tw:text-sm tw:text-ed-stone'>
                    Nothing free on this date. Try another day.
                  </p>
                ) : (
                  <div className='tw:mt-4 tw:flex tw:flex-col tw:gap-2'>
                    {/* One per row, each a single line: the label and the hours
                        sit side by side rather than stacked, so three rows take
                        less height than the two rows of two-line cards they
                        replace. */}
                    {options.map((o) => (
                      <button
                        key={o.key}
                        type='button'
                        onClick={() => setOption(o)}
                        className={cn(
                          'tw:flex tw:flex-wrap tw:items-baseline tw:justify-between tw:gap-x-4 tw:gap-y-1 tw:rounded-sm tw:border-2 tw:px-4 tw:py-3 tw:text-left tw:transition-all',
                          option?.key === o.key
                            ? 'tw:border-ed-gold tw:bg-ed-gold/10'
                            : 'tw:border-ed-umber/20 tw:hover:border-ed-gold',
                        )}
                      >
                        <span className='tw:font-serif tw:text-lg tw:text-ed-black'>{o.label}</span>
                        <span className='tw:text-sm tw:text-ed-stone'>{o.hours}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {option && (
              <div className='tw:mt-6 tw:rounded-sm tw:bg-ed-cream tw:p-5'>
                <h4 className='tw:font-serif tw:text-lg tw:text-ed-black'>Request this tour leader</h4>
                <dl className='tw:mt-3 tw:space-y-1 tw:text-sm'>
                  <SummaryRow label='Tour Leader' value={guide.name} />
                  <SummaryRow label='Date' value={formatCalendarDate(date)} />
                  <SummaryRow label='Tour' value={`${option.label} (${option.hours})`} />
                </dl>

                {/* TODO(guide bookings): there is no request path for a guide
                    yet. `bookingRequests` is shaped around an experience — it
                    carries an experienceId, an availability slot and a
                    cancellation policy taken from that experience — so a guide
                    booking is not a variant of one; it needs its own collection,
                    callable and admin queue. Until that exists this hands the
                    concierge everything they would have typed into the form, in
                    an email, rather than storing a request nobody is watching.
                    The static prototype's version wrote to localStorage, which
                    looked like it worked and reached no one. */}
                <a
                  href={guideRequestMailto(guide, date, option)}
                  className='tw:mt-5 tw:flex tw:w-full tw:items-center tw:justify-center tw:gap-2 tw:bg-ed-gold tw:py-3.5 tw:text-sm tw:font-medium tw:tracking-widest tw:text-ed-black tw:uppercase tw:transition-all tw:hover:bg-ed-black tw:hover:text-ed-cream'
                >
                  <Mail className='tw:size-4' /> Email this request
                </a>
                <p className='tw:mt-3 tw:text-xs tw:text-ed-stone'>
                  Tour leader requests are handled by our team directly — we will confirm within 24 hours.
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='tw:flex tw:justify-between tw:gap-4'>
      <dt className='tw:text-ed-stone'>{label}</dt>
      <dd className='tw:text-right tw:text-ed-black'>{value}</dd>
    </div>
  );
}

function Notice({
  icon: Icon,
  tone,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  tone: 'gold' | 'stone' | 'red';
  children: React.ReactNode;
}) {
  const toneClass = {
    gold: 'tw:border-ed-gold/30 tw:bg-ed-gold/5 tw:text-ed-umber',
    stone: 'tw:border-ed-stone/30 tw:bg-ed-stone/5 tw:text-ed-charcoal',
    red: 'tw:border-ed-red/30 tw:bg-ed-red/5 tw:text-ed-red',
  }[tone];

  return (
    <div className={`tw:mb-6 tw:flex tw:items-start tw:gap-3 tw:rounded tw:border tw:px-4 tw:py-3 tw:text-sm ${toneClass}`}>
      <Icon className='tw:mt-0.5 tw:size-4 tw:shrink-0' />
      <p>{children}</p>
    </div>
  );
}

/**
 * The availability panel while /portal's calendar sync is still being wired up
 * (see `liveGuideAvailability`).
 *
 * It says the plain thing — this is coming, here is who to ask meanwhile — and
 * hands over the desk number and a pre-written email rather than leaving the
 * concierge to find them in the footer. The greyed calendar underneath shows
 * what will be here without pretending it already is.
 */
function ComingSoonAvailability({
  guide,
  fromDate,
  toDate,
}: {
  guide: GuideType;
  fromDate: string;
  toDate: string;
}) {
  const firstName = guide.name.split(' ')[0];

  return (
    <>
      <Notice icon={CalendarClock} tone='gold'>
        Live availability is coming soon. To find out which days {firstName} is free,{' '}
        {/* The number is hidden rather than shown dead when it is not configured
            — see `contactPhone`. */}
        {contactPhone && (
          <>
            call us on <ContactLink href={`tel:${contactPhone}`}>{contactPhoneLabel()}</ContactLink> or{' '}
          </>
        )}
        email <ContactLink href={guideEnquiryMailto(guide)}>{contactEmail}</ContactLink>, and we will check
        the diary and come back to you.
      </Notice>

      <GuideAvailabilityCalendar diary={null} fromDate={fromDate} toDate={toDate} value='' />
    </>
  );
}

function ContactLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} className='tw:font-medium tw:whitespace-nowrap tw:underline tw:underline-offset-2'>
      {children}
    </a>
  );
}

/**
 * An enquiry with no date on it, for the coming-soon path.
 *
 * The date and tour length are prompts here rather than filled-in facts: without
 * a diary the concierge is the one who knows what they are after, and an email
 * that asks for it beats one that arrives saying only "availability?".
 */
function guideEnquiryMailto(guide: GuideType): string {
  const subject = `Tour leader availability — ${guide.name}`;
  const body = [
    `Tour leader: ${guide.name}`,
    '',
    'Preferred date(s):',
    'Full day or half day:',
    'Guest name:',
    'Number of guests:',
    'Special requests:',
  ].join('\n');

  return `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** The request, pre-written, so nothing has to be retyped or remembered. */
function guideRequestMailto(guide: GuideType, date: string, option: GuideTourOptionType): string {
  const subject = `Tour leader request — ${guide.name}, ${formatCalendarDate(date)}`;
  const body = [
    `Tour leader: ${guide.name}`,
    `Date: ${formatCalendarDate(date)}`,
    `Tour: ${option.label} (${option.hours})`,
    '',
    'Guest name:',
    'Number of guests:',
    'Special requests:',
  ].join('\n');

  return `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
